import { FieldValues, get, Path, useFormContext } from 'react-hook-form';

interface FormTabHeadingsProps<T extends string, Fields extends FieldValues = FieldValues> {
  tabs: T[];
  currentTab: T;
  conditions?: Partial<Record<T, () => boolean>>;
  errorKeys?: Partial<Record<T, Path<Fields>[]>>;
  handleTabSelected: (tab: T) => () => void;
}

export const FormTabHeadings = <T extends string, K extends FieldValues = FieldValues>({ tabs, currentTab, conditions, errorKeys, handleTabSelected }: FormTabHeadingsProps<T, K>) => {
  const {
    formState: { errors }
  } = useFormContext();

  const renderTab = (tab: T) => {
    if (conditions) {
      const condition = conditions[tab];
      if (condition !== undefined && !condition()) {
        return null;
      }
    }

    let errorsForTab = undefined;
    const keysForTab = errorKeys ? errorKeys[tab] : [];
    if (keysForTab && keysForTab.length > 0) {
      errorsForTab = keysForTab.reduce((acc, errorKey) => {
        const errorsForKey = get(errors, errorKey);
        if (!errorsForKey) return acc;
        return acc + Object.keys(get(errors, errorKey)).length;
      }, 0);
    }

    const isActive = currentTab === tab;

    const className = [
      'border-t-[2px] border-x-[2px] cursor-pointer px-3 pb-2 pt-1',
      'transition-all duration-150 select-none',
      isActive
        ? 'border-[1px] border-white bg-secondary-deep-blue-80 border-t-secondary-deep-blue-80 border-x-secondary-deep-blue-80 text-white font-[500] -translate-y-0.5 shadow-[0_-2px_8px_rgba(0,0,0,0.25)] z-10 relative'
        : `border-x-transparent border-t-transparent hover:bg-secondary-deep-blue-80/30 ${errorsForTab ? 'text-feedback-red' : 'text-solid-white'}`
    ].join(' ');

    return (
      <div key={tab} className={className} onClick={handleTabSelected(tab)}>
        {tab}
      </div>
    );
  };

  return <div className="flex flex-row w-full text-nowrap items-end">{tabs.map(renderTab)}</div>;
};
